/**
 * The get-going card's progress, per business, held in memory over the row on
 * disk.
 *
 * The row in `sync_meta` is the truth (`lib/setup-progress.ts`); this store is
 * the copy the Dashboard renders from, so a skip repaints on the tap and the
 * card does not read the database on every frame. A zustand store and not
 * React state, for the reason `sync-store` is one: two of the writes happen
 * from an effect that is syncing what the frame showed — which steps were seen
 * pending, and that the last one is done — and an effect that sets React state
 * is the cascade the compiler lint refuses. Updating an external store from an
 * effect is what effects are for.
 *
 * Every write goes to the row on the same tick, so a crash a second later
 * cannot undo a skip. Never persisted by itself: the row is the persistence.
 * `reset()` at sign-out, beside `useSyncStore.reset()`, so the next account on
 * this phone starts from the rows `forgetDevice()` left, which is none.
 */
import { create } from 'zustand';

import { getSetupProgress, saveSetupProgress } from '@/lib/setup-progress';
import { EMPTY_PROGRESS, type SetupProgress } from '@/lib/setup-steps';

type SetupProgressState = {
  /** tenantId -> the progress as last read or written. Absent until `load`. */
  byTenant: Record<string, SetupProgress>;
  /** Read the row into memory, once. A database not up yet reads as empty. */
  load: (tenantId: string) => void;
  /** The new progress for this frame, and the row for the next launch. */
  write: (tenantId: string, next: SetupProgress) => void;
  reset: () => void;
};

export const useSetupProgressStore = create<SetupProgressState>()((set, get) => ({
  byTenant: {},

  load: (tenantId) => {
    if (get().byTenant[tenantId] !== undefined) return;
    let progress = EMPTY_PROGRESS;
    try {
      progress = getSetupProgress(tenantId);
    } catch {
      // The card draws fresh. The next launch reads again.
    }
    set((state) => ({ byTenant: { ...state.byTenant, [tenantId]: progress } }));
  },

  write: (tenantId, next) => {
    if (get().byTenant[tenantId] === next) return;
    set((state) => ({ byTenant: { ...state.byTenant, [tenantId]: next } }));
    try {
      saveSetupProgress(tenantId, next);
    } catch {
      // The frame already shows the change. The next launch asks again, which
      // is a skip repeated and not a skip lost.
    }
  },

  reset: () => set({ byTenant: {} }),
}));

/** The progress this frame renders from, or the empty one until the row is loaded. */
export const useSetupProgress = (tenantId: string): SetupProgress =>
  useSetupProgressStore((state) => state.byTenant[tenantId] ?? EMPTY_PROGRESS);
